import { useMemo } from 'react';
import type { DisplayRoom } from '@/types/supplement';
import type { SupplementDetailed } from '@/api/generated/client/models';

export interface CategoryStatistics {
  omitted: { count: number; amount: number };
  underscoped: { count: number; amount: number; rcvDifference: number };
  freeScoped: { count: number; amount: number };
  overscoped: { count: number; amount: number; rcvDifference: number };
  perfectMatch: { count: number; amount: number };
}

export interface FinancialTotals {
  supplementPrice: number;
  contractorTotal: number;
  insuranceTotal: number;
  rcvDifference: number;
}

export interface JustificationCounts {
  codeBasedCount: number;
  generalCount: number;
}

/**
 * Hook to calculate category statistics from room data
 * For underscoped/overscoped, we now track the RCV difference between matched items
 */
export function useCategoryStatistics(rooms: DisplayRoom[]): CategoryStatistics {
  return useMemo(() => {
    const stats = rooms.reduce((acc, room) => {
      room.items.forEach(item => {
        const category = item.category;
        
        if (!acc[category]) {
          acc[category] = { count: 0, amount: 0, rcvDifference: 0 };
        }
        
        acc[category].count += 1;
        
        // Calculate amount based on category
        if (category === 'unmatched_contractor') {
          // For unmatched contractor items, use the full contractor amount
          const contractorTotal = item.contractorItems.reduce((sum, ci) => sum + ci.rcvCents, 0);
          acc[category].amount += contractorTotal;
        } else if (category === 'unmatched_insurer') {
          // For unmatched insurer items (free scoped), use the full insurer amount
          const insurerTotal = item.insurerItems.reduce((sum, ii) => sum + ii.rcvCents, 0);
          acc[category].amount += insurerTotal;
        } else if (category === 'underscoped') {
          // For underscoped, use the difference (supplement amount)
          acc[category].amount += item.amountCents;
          // Calculate RCV difference for matched items
          const contractorRcv = item.contractorItems.reduce((sum, ci) => sum + ci.rcvCents, 0);
          const insurerRcv = item.insurerItems.reduce((sum, ii) => sum + ii.rcvCents, 0);
          acc[category].rcvDifference += Math.abs(contractorRcv - insurerRcv);
        } else if (category === 'overscoped') {
          // For overscoped, use the absolute difference
          acc[category].amount += Math.abs(item.amountCents);
          // Calculate RCV difference for matched items
          const contractorRcv = item.contractorItems.reduce((sum, ci) => sum + ci.rcvCents, 0);
          const insurerRcv = item.insurerItems.reduce((sum, ii) => sum + ii.rcvCents, 0);
          acc[category].rcvDifference += Math.abs(contractorRcv - insurerRcv);
        } else if (category === 'perfect_match') {
          // For perfect matches, use contractor total (both sides are equal)
          const contractorTotal = item.contractorItems.reduce((sum, ci) => sum + ci.rcvCents, 0);
          acc[category].amount += contractorTotal;
        }
      });
      return acc;
    }, {} as Record<string, { count: number; amount: number; rcvDifference: number }>);

    return {
      omitted: stats.unmatched_contractor || { count: 0, amount: 0 },
      underscoped: stats.underscoped || { count: 0, amount: 0, rcvDifference: 0 },
      freeScoped: stats.unmatched_insurer || { count: 0, amount: 0 },
      overscoped: stats.overscoped || { count: 0, amount: 0, rcvDifference: 0 },
      perfectMatch: stats.perfect_match || { count: 0, amount: 0 },
    };
  }, [rooms]);
}

/**
 * Hook to calculate financial totals from category statistics and rooms
 * Now returns contractor total, insurance total, and RCV difference instead of baseline
 */
export function useFinancialTotals(categoryStats: CategoryStatistics, rooms: DisplayRoom[]): FinancialTotals {
  return useMemo(() => {
    // Supplement price = unmatched contractor (full amount) + underscoped difference (contractor - insurer)
    // This matches the calculation in useSupplementTransform.ts
    const supplementPrice = rooms.reduce((total, room) => {
      return total + room.items
        .filter(i => i.category === 'unmatched_contractor' || i.category === 'underscoped')
        .reduce((sum, i) => {
          const contractorTotal = i.contractorItems.reduce((s, item) => s + item.rcvCents, 0);
          const insurerTotal = i.insurerItems.reduce((s, item) => s + item.rcvCents, 0);
          return sum + (contractorTotal - insurerTotal);
        }, 0);
    }, 0);
    
    // Calculate contractor total: sum of all contractor RCVs across all items
    const contractorTotal = rooms.reduce((total, room) => {
      return total + room.items.reduce((roomTotal, item) => {
        const contractorRcv = item.contractorItems.reduce((sum, ci) => sum + ci.rcvCents, 0);
        return roomTotal + contractorRcv;
      }, 0);
    }, 0);

    // Calculate insurance total: sum of all insurer RCVs across matched items + free scoped
    const insuranceTotal = rooms.reduce((total, room) => {
      return total + room.items.reduce((roomTotal, item) => {
        const insurerRcv = item.insurerItems.reduce((sum, ii) => sum + ii.rcvCents, 0);
        return roomTotal + insurerRcv;
      }, 0);
    }, 0);

    // RCV Difference: contractor total - insurance total
    const rcvDifference = contractorTotal - insuranceTotal;

    return {
      supplementPrice,
      contractorTotal,
      insuranceTotal,
      rcvDifference,
    };
  }, [rooms]);
}

/**
 * Hook to count code-based vs general justifications
 * Code-based justifications have justificationSource === 'codebook' or have codeBookTitle
 */
export function useJustificationCounts(supplement: SupplementDetailed): JustificationCounts {
  return useMemo(() => {
    if (!supplement.justifications || supplement.justifications.length === 0) {
      return { codeBasedCount: 0, generalCount: 0 };
    }

    const counts = supplement.justifications.reduce((acc, justification) => {
      // Code-based justifications have justificationSource === 'codebook' or have codeBookTitle
      const isCodeBased = justification.justificationSource === 'codebook' || 
                          (justification.codeBookTitle != null && justification.codeBookTitle.trim() !== '');
      
      if (isCodeBased) {
        acc.codeBasedCount += 1;
      } else {
        acc.generalCount += 1;
      }
      
      return acc;
    }, { codeBasedCount: 0, generalCount: 0 });

    return counts;
  }, [supplement.justifications]);
}

